/**
 * Wrapped MUI primitives — the single source of UI components for every web app.
 *
 * Apps import these from `@munifocus/ui`, never from `@mui/material` directly
 * (enforced by ESLint `no-restricted-imports`). Each export is a thin
 * `forwardRef` render wrapper around the corresponding MUI component: visual
 * design stays driven by the active theme, while the app-facing symbol is
 * ours — so defaults can be injected here, props can be locked down, or the
 * underlying implementation can be swapped, all in one place.
 *
 * Each export is cast to the MUI component's own type (`typeof MuiX`) so that
 * MUI's polymorphic `component` prop and ref typing carry through unchanged
 * (e.g. `<Box component="form" noValidate>`, `<Link component={RouterLink}>`).
 *
 * To expose a new component to the apps, add a wrapper here and re-export it
 * from `index.ts`. Do not re-open a direct `@mui/material` import in an app.
 */
import { forwardRef } from 'react';
import { useA11yLabels } from './a11y-labels';
import {
  Alert as MuiAlert,
  AlertTitle as MuiAlertTitle,
  Avatar as MuiAvatar,
  Badge as MuiBadge,
  Box as MuiBox,
  Button as MuiButton,
  Checkbox as MuiCheckbox,
  Chip as MuiChip,
  Autocomplete as MuiAutocomplete,
  CircularProgress as MuiCircularProgress,
  Collapse as MuiCollapse,
  Container as MuiContainer,
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogContentText as MuiDialogContentText,
  DialogTitle as MuiDialogTitle,
  Divider as MuiDivider,
  Drawer as MuiDrawer,
  FormControl as MuiFormControl,
  FormControlLabel as MuiFormControlLabel,
  FormHelperText as MuiFormHelperText,
  FormLabel as MuiFormLabel,
  GlobalStyles as MuiGlobalStyles,
  Grid as MuiGrid,
  IconButton as MuiIconButton,
  InputAdornment as MuiInputAdornment,
  InputLabel as MuiInputLabel,
  Link as MuiLink,
  List as MuiList,
  ListItem as MuiListItem,
  ListSubheader as MuiListSubheader,
  ListItemAvatar as MuiListItemAvatar,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Menu as MuiMenu,
  Paper as MuiPaper,
  Popper as MuiPopper,
  MenuItem as MuiMenuItem,
  Pagination as MuiPagination,
  Select as MuiSelect,
  Slider as MuiSlider,
  Snackbar as MuiSnackbar,
  Stack as MuiStack,
  Step as MuiStep,
  StepLabel as MuiStepLabel,
  Stepper as MuiStepper,
  Switch as MuiSwitch,
  Tab as MuiTab,
  Tabs as MuiTabs,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TableSortLabel as MuiTableSortLabel,
  TextField as MuiTextField,
  Tooltip as MuiTooltip,
  Typography as MuiTypography,
  type AlertProps,
  type AlertTitleProps,
  type AvatarProps,
  type BadgeProps,
  type BoxProps,
  type ButtonProps,
  type CheckboxProps,
  type ChipProps,
  type AutocompleteProps,
  type CircularProgressProps,
  type CollapseProps,
  type ContainerProps,
  type DialogProps,
  type DialogActionsProps,
  type DialogContentProps,
  type DialogContentTextProps,
  type DialogTitleProps,
  type DividerProps,
  type DrawerProps,
  type FormControlProps,
  type FormControlLabelProps,
  type FormHelperTextProps,
  type FormLabelProps,
  type GlobalStylesProps,
  type GridProps,
  type IconButtonProps,
  type InputAdornmentProps,
  type InputLabelProps,
  type LinkProps,
  type ListProps,
  type ListItemProps,
  type ListItemAvatarProps,
  type ListItemButtonProps,
  type ListItemIconProps,
  type ListItemTextProps,
  type ListSubheaderProps,
  type MenuProps,
  type PaperProps,
  type PopperProps,
  type MenuItemProps,
  type PaginationProps,
  type SelectProps,
  type SliderProps,
  type SnackbarProps,
  type StackProps,
  type StepProps,
  type StepLabelProps,
  type StepperProps,
  type SwitchProps,
  type TabProps,
  type TabsProps,
  type ToggleButtonProps,
  type ToggleButtonGroupProps,
  type TableProps,
  type TableBodyProps,
  type TableCellProps,
  type TableHeadProps,
  type TableRowProps,
  type TableSortLabelProps,
  type TextFieldProps,
  type TooltipProps,
  type TypographyProps,
} from '@mui/material';

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert ref={ref} {...props} />;
}) as unknown as typeof MuiAlert;

export const AlertTitle = forwardRef<HTMLDivElement, AlertTitleProps>(
  function AlertTitle(props, ref) {
    return <MuiAlertTitle ref={ref} {...props} />;
  },
) as unknown as typeof MuiAlertTitle;

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(props, ref) {
  return <MuiAvatar ref={ref} {...props} />;
}) as unknown as typeof MuiAvatar;

export const Box = forwardRef<HTMLElement, BoxProps>(function Box(props, ref) {
  return <MuiBox ref={ref as never} {...props} />;
}) as unknown as typeof MuiBox;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  return <MuiButton ref={ref} {...props} />;
}) as unknown as typeof MuiButton;

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(props, ref) {
  return <MuiCheckbox ref={ref} {...props} />;
}) as unknown as typeof MuiCheckbox;

export const Chip = forwardRef<HTMLDivElement, ChipProps>(function Chip(props, ref) {
  return <MuiChip ref={ref} {...props} />;
}) as unknown as typeof MuiChip;

/**
 * MUI renders `role="progressbar"`, which is only conformant with an accessible
 * name (WCAG 1.1.1). Almost every call site is a bare `<CircularProgress />`, so
 * default the name here rather than at ~160 call sites; `useA11yLabels` supplies
 * the app's translation. An explicit `aria-label`/`aria-labelledby` still wins.
 */
export const CircularProgress = forwardRef<HTMLSpanElement, CircularProgressProps>(
  function CircularProgress(props, ref) {
    const { loading } = useA11yLabels();
    const named =
      props['aria-label'] === undefined && props['aria-labelledby'] === undefined
        ? { 'aria-label': loading }
        : undefined;
    return <MuiCircularProgress ref={ref} {...named} {...props} />;
  },
) as unknown as typeof MuiCircularProgress;

export const Collapse = forwardRef<HTMLDivElement, CollapseProps>(function Collapse(props, ref) {
  return <MuiCollapse ref={ref as never} {...props} />;
}) as unknown as typeof MuiCollapse;

export const Container = forwardRef<HTMLDivElement, ContainerProps>(function Container(props, ref) {
  return <MuiContainer ref={ref as never} {...props} />;
}) as unknown as typeof MuiContainer;

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(props, ref) {
  return <MuiDialog ref={ref} {...props} />;
}) as unknown as typeof MuiDialog;

export const DialogTitle = forwardRef<HTMLHeadingElement, DialogTitleProps>(
  function DialogTitle(props, ref) {
    return <MuiDialogTitle ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiDialogTitle;

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent(props, ref) {
    return <MuiDialogContent ref={ref} {...props} />;
  },
) as unknown as typeof MuiDialogContent;

export const DialogContentText = forwardRef<HTMLSpanElement, DialogContentTextProps>(
  function DialogContentText(props, ref) {
    return <MuiDialogContentText ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiDialogContentText;

export const DialogActions = forwardRef<HTMLDivElement, DialogActionsProps>(
  function DialogActions(props, ref) {
    return <MuiDialogActions ref={ref} {...props} />;
  },
) as unknown as typeof MuiDialogActions;

export const Divider = forwardRef<HTMLHRElement, DividerProps>(function Divider(props, ref) {
  return <MuiDivider ref={ref as never} {...props} />;
}) as unknown as typeof MuiDivider;

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(props, ref) {
  return <MuiDrawer ref={ref} {...props} />;
}) as unknown as typeof MuiDrawer;

export const FormControl = forwardRef<HTMLDivElement, FormControlProps>(
  function FormControl(props, ref) {
    return <MuiFormControl ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiFormControl;

export const FormControlLabel = forwardRef<HTMLLabelElement, FormControlLabelProps>(
  function FormControlLabel(props, ref) {
    return <MuiFormControlLabel ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiFormControlLabel;

// The legend over a group of controls, and the message under it. Needed
// wherever a fieldset replaces a single input — a set of checkboxes has no
// `label`/`helperText` of its own the way a TextField does.
export const FormLabel = forwardRef<HTMLLabelElement, FormLabelProps>(
  function FormLabel(props, ref) {
    return <MuiFormLabel ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiFormLabel;

export const FormHelperText = forwardRef<HTMLParagraphElement, FormHelperTextProps>(
  function FormHelperText(props, ref) {
    return <MuiFormHelperText ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiFormHelperText;

export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(props, ref) {
  return <MuiGrid ref={ref as never} {...props} />;
}) as unknown as typeof MuiGrid;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(props, ref) {
    return <MuiIconButton ref={ref} {...props} />;
  },
) as unknown as typeof MuiIconButton;

export const InputAdornment = forwardRef<HTMLDivElement, InputAdornmentProps>(
  function InputAdornment(props, ref) {
    return <MuiInputAdornment ref={ref} {...props} />;
  },
) as unknown as typeof MuiInputAdornment;

export const InputLabel = forwardRef<HTMLLabelElement, InputLabelProps>(
  function InputLabel(props, ref) {
    return <MuiInputLabel ref={ref} {...props} />;
  },
) as unknown as typeof MuiInputLabel;

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(props, ref) {
  return <MuiBadge ref={ref} {...props} />;
}) as unknown as typeof MuiBadge;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  return <MuiLink ref={ref as never} {...props} />;
}) as unknown as typeof MuiLink;

export const List = forwardRef<HTMLUListElement, ListProps>(function List(props, ref) {
  return <MuiList ref={ref} {...props} />;
}) as unknown as typeof MuiList;

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(function ListItem(props, ref) {
  return <MuiListItem ref={ref} {...props} />;
}) as unknown as typeof MuiListItem;

/**
 * A heading inside a menu or list — the organisation a group of people belong
 * to, say.
 *
 * `disableSticky` by default: a `<Select>`'s menu is short and scrolls in its
 * own popover, where a pinned heading covers the option beneath it rather than
 * helping anyone keep their place.
 */
export const ListSubheader = forwardRef<HTMLLIElement, ListSubheaderProps>(function ListSubheader(
  { disableSticky = true, ...props },
  ref,
) {
  return <MuiListSubheader ref={ref} disableSticky={disableSticky} {...props} />;
}) as unknown as typeof MuiListSubheader;

export const ListItemAvatar = forwardRef<HTMLDivElement, ListItemAvatarProps>(
  function ListItemAvatar(props, ref) {
    return <MuiListItemAvatar ref={ref} {...props} />;
  },
) as unknown as typeof MuiListItemAvatar;

export const ListItemButton = forwardRef<HTMLDivElement, ListItemButtonProps>(
  function ListItemButton(props, ref) {
    return <MuiListItemButton ref={ref as never} {...props} />;
  },
) as unknown as typeof MuiListItemButton;

export const ListItemIcon = forwardRef<HTMLDivElement, ListItemIconProps>(
  function ListItemIcon(props, ref) {
    return <MuiListItemIcon ref={ref} {...props} />;
  },
) as unknown as typeof MuiListItemIcon;

export const ListItemText = forwardRef<HTMLDivElement, ListItemTextProps>(
  function ListItemText(props, ref) {
    return <MuiListItemText ref={ref} {...props} />;
  },
) as unknown as typeof MuiListItemText;

export const Menu = forwardRef<HTMLDivElement, MenuProps>(function Menu(props, ref) {
  return <MuiMenu ref={ref} {...props} />;
}) as unknown as typeof MuiMenu;

/**
 * An anchored panel that does **not** take over the page.
 *
 * Deliberately `Popper` rather than `Popover`: Popover renders inside a Modal,
 * which marks the rest of the application `aria-hidden` and swallows clicks.
 * That is right for a dialog and wrong for an explanatory bubble — an officer
 * being told what a control does should still be able to use the portal, and a
 * screen reader should not lose the page behind the explanation.
 *
 * `Menu` is out for a second reason: it carries menu semantics, so prose inside
 * it is announced as a set of commands.
 */
export const Popper = forwardRef<HTMLDivElement, PopperProps>(function Popper(props, ref) {
  return <MuiPopper ref={ref} {...props} />;
}) as unknown as typeof MuiPopper;

export const Paper = forwardRef<HTMLDivElement, PaperProps>(function Paper(props, ref) {
  return <MuiPaper ref={ref} {...props} />;
}) as unknown as typeof MuiPaper;

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(function MenuItem(props, ref) {
  return <MuiMenuItem ref={ref} {...props} />;
}) as unknown as typeof MuiMenuItem;

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(props, ref) {
  return <MuiSelect ref={ref as never} {...props} />;
}) as unknown as typeof MuiSelect;

export const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(function Snackbar(props, ref) {
  return <MuiSnackbar ref={ref} {...props} />;
}) as unknown as typeof MuiSnackbar;

export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(props, ref) {
  return <MuiStack ref={ref as never} {...props} />;
}) as unknown as typeof MuiStack;

export const Step = forwardRef<HTMLDivElement, StepProps>(function Step(props, ref) {
  return <MuiStep ref={ref} {...props} />;
}) as unknown as typeof MuiStep;

export const StepLabel = forwardRef<HTMLDivElement, StepLabelProps>(function StepLabel(props, ref) {
  return <MuiStepLabel ref={ref} {...props} />;
}) as unknown as typeof MuiStepLabel;

export const Stepper = forwardRef<HTMLDivElement, StepperProps>(function Stepper(props, ref) {
  return <MuiStepper ref={ref} {...props} />;
}) as unknown as typeof MuiStepper;

/** Keyboard-operable range input (arrows, Page Up/Down, Home/End) — the history replay scrubber. */
export const Slider = forwardRef<HTMLSpanElement, SliderProps>(function Slider(props, ref) {
  return <MuiSlider ref={ref} {...props} />;
}) as unknown as typeof MuiSlider;

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(props, ref) {
  return <MuiSwitch ref={ref} {...props} />;
}) as unknown as typeof MuiSwitch;

export const Tab = forwardRef<HTMLDivElement, TabProps>(function Tab(props, ref) {
  return <MuiTab ref={ref} {...props} />;
}) as unknown as typeof MuiTab;

/**
 * Scrollable by default. MUI's standard variant clips whatever does not fit and
 * offers no way to reach it, so at phone width the last tabs of a strip — the
 * admin Users page's "Public" tab, Billing's "Subscriptions", longer still in
 * French — were simply unreachable. A strip that fits looks the same either
 * way; a caller that wants `fullWidth` still passes it.
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(props, ref) {
  const scrollable = !props.variant || props.variant === 'scrollable';
  return (
    <MuiTabs
      ref={ref}
      {...(scrollable && !props.centered
        ? {
            variant: 'scrollable' as const,
            scrollButtons: 'auto' as const,
            allowScrollButtonsMobile: true,
          }
        : {})}
      {...props}
    />
  );
}) as unknown as typeof MuiTabs;

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  function ToggleButton(props, ref) {
    return <MuiToggleButton ref={ref} {...props} />;
  },
) as unknown as typeof MuiToggleButton;

export const ToggleButtonGroup = forwardRef<HTMLDivElement, ToggleButtonGroupProps>(
  function ToggleButtonGroup(props, ref) {
    return <MuiToggleButtonGroup ref={ref} {...props} />;
  },
) as unknown as typeof MuiToggleButtonGroup;

export const Pagination = forwardRef<HTMLElement, PaginationProps>(function Pagination(props, ref) {
  return <MuiPagination ref={ref} {...props} />;
}) as unknown as typeof MuiPagination;

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(props, ref) {
  return <MuiTable ref={ref} {...props} />;
}) as unknown as typeof MuiTable;

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  function TableBody(props, ref) {
    return <MuiTableBody ref={ref} {...props} />;
  },
) as unknown as typeof MuiTableBody;

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  function TableCell(props, ref) {
    return <MuiTableCell ref={ref} {...props} />;
  },
) as unknown as typeof MuiTableCell;

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  function TableHead(props, ref) {
    return <MuiTableHead ref={ref} {...props} />;
  },
) as unknown as typeof MuiTableHead;

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  function TableRow(props, ref) {
    return <MuiTableRow ref={ref} {...props} />;
  },
) as unknown as typeof MuiTableRow;

export const TableSortLabel = forwardRef<HTMLSpanElement, TableSortLabelProps>(
  function TableSortLabel(props, ref) {
    return <MuiTableSortLabel ref={ref} {...props} />;
  },
) as unknown as typeof MuiTableSortLabel;

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(function TextField(props, ref) {
  return <MuiTextField ref={ref} {...props} />;
}) as unknown as typeof MuiTextField;

/**
 * Re-exported rather than restyled: the generics carry the option type through
 * to `renderOption`, and a wrapper that erased them would make every call site
 * cast. Apps still import it from here, never from `@mui/material`.
 */
export const Autocomplete = MuiAutocomplete;
export type { AutocompleteProps };

export const Tooltip = forwardRef<unknown, TooltipProps>(function Tooltip(props, ref) {
  return <MuiTooltip ref={ref as never} {...props} />;
}) as unknown as typeof MuiTooltip;

export const Typography = forwardRef<HTMLElement, TypographyProps>(function Typography(props, ref) {
  return <MuiTypography ref={ref as never} {...props} />;
}) as unknown as typeof MuiTypography;

// GlobalStyles injects a <style> tag and takes no ref — plain passthrough.
export const GlobalStyles = function GlobalStyles(props: GlobalStylesProps) {
  return <MuiGlobalStyles {...props} />;
} as typeof MuiGlobalStyles;

export type {
  AlertProps,
  AlertTitleProps,
  AvatarProps,
  BoxProps,
  ButtonProps,
  BadgeProps,
  CheckboxProps,
  ChipProps,
  CircularProgressProps,
  CollapseProps,
  ContainerProps,
  DialogProps,
  DialogActionsProps,
  DialogContentProps,
  DialogContentTextProps,
  DialogTitleProps,
  DividerProps,
  DrawerProps,
  FormControlProps,
  FormControlLabelProps,
  FormHelperTextProps,
  FormLabelProps,
  GlobalStylesProps,
  GridProps,
  IconButtonProps,
  InputAdornmentProps,
  InputLabelProps,
  LinkProps,
  ListProps,
  ListItemProps,
  ListItemAvatarProps,
  ListItemButtonProps,
  ListItemIconProps,
  ListItemTextProps,
  MenuProps,
  MenuItemProps,
  PaginationProps,
  SelectProps,
  SnackbarProps,
  StackProps,
  StepProps,
  StepLabelProps,
  StepperProps,
  SwitchProps,
  ToggleButtonProps,
  ToggleButtonGroupProps,
  TableProps,
  TableBodyProps,
  TableCellProps,
  TableHeadProps,
  TableRowProps,
  TableSortLabelProps,
  TextFieldProps,
  TooltipProps,
  TypographyProps,
};
